export interface ProjectionInput {
  initial: number;
  monthlyContribution: number;
  years: number;
  annualReturn: number;
  inflationAdjusted?: boolean;
}

export interface ProjectionPoint {
  year: number;
  value: number;
  principal: number;
  gains: number;
}

export interface ProjectionSummary {
  finalValue: number;
  totalContributed: number;
  totalGrowth: number;
  growthMultiple: number;
}

const INFLATION_RATE = 0.025;

export function computeProjection({
  initial,
  monthlyContribution,
  years,
  annualReturn,
  inflationAdjusted = false,
}: ProjectionInput): ProjectionPoint[] {
  const effectiveReturn = inflationAdjusted ? annualReturn - INFLATION_RATE : annualReturn;
  const monthlyRate = effectiveReturn / 12;

  const points: ProjectionPoint[] = [
    { year: 0, value: initial, principal: initial, gains: 0 },
  ];

  let balance = initial;
  let principal = initial;

  for (let year = 1; year <= years; year++) {
    for (let month = 0; month < 12; month++) {
      balance = balance * (1 + monthlyRate) + monthlyContribution;
      principal += monthlyContribution;
    }
    points.push({
      year,
      value: Math.round(balance * 100) / 100,
      principal: Math.round(principal * 100) / 100,
      gains: Math.round((balance - principal) * 100) / 100,
    });
  }

  return points;
}

export function summarize(projection: ProjectionPoint[]): ProjectionSummary {
  const last = projection[projection.length - 1];
  const first = projection[0];
  const finalValue = last.value;
  const totalContributed = last.principal;
  const totalGrowth = finalValue - totalContributed;
  const growthMultiple = first.principal > 0 || totalContributed > 0
    ? finalValue / totalContributed
    : 0;

  return {
    finalValue,
    totalContributed,
    totalGrowth,
    growthMultiple,
  };
}
